import { parseColor } from '@ark-ui/react'
import { ColorPicker as ColorPickerPrimitive } from '@ark-ui/react/color-picker'
import { ReactElement, SVGProps, useContext } from 'react'
import { cn } from 'utils/cn'
import { createContextWithProvider, createStateContext } from 'utils/context'

import { Input } from '../Input'

export type TColorInputFormat = 'hex' | 'rgb' | 'hsl'

export const [ColorPickerValueContext, ColorPickerValueProvider] =
	createContextWithProvider<{
		value: string
		setValue: (color: string) => void
	}>('ColorPickerValue')

export const [ColorPickerFormatContext, ColorPickerFormatProvider] =
	createStateContext<TColorInputFormat>({
		name: 'ColorPickerFormat',
		defaultValue: 'rgb',
	})

const IconSelect = (props: SVGProps<SVGSVGElement>) => (
	<svg
		width={5}
		height={12}
		viewBox="0 0 5 12"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		{...props}
	>
		<g clipPath="url(#a)">
			<path
				d="M2.5 1.325c.09 0 .18.034.248.103l2.15 2.15a.35.35 0 1 1-.496.495L2.5 2.17.598 4.073a.35.35 0 0 1-.495-.496l2.15-2.15a.35.35 0 0 1 .247-.102"
				fill="currentColor"
			/>
		</g>
		<g clipPath="url(#b)">
			<path
				d="M2.5 10.675a.35.35 0 0 1-.248-.103l-2.15-2.15a.35.35 0 1 1 .496-.495L2.5 9.83l1.902-1.903a.35.35 0 0 1 .495.495l-2.15 2.15a.35.35 0 0 1-.247.103"
				fill="currentColor"
			/>
		</g>
	</svg>
)

const colorInputCls = cn(
	'type-body-small h-[unset] w-full rounded-full px-2 py-1 text-center',
)

const InputHex = () => (
	<ColorPickerPrimitive.ChannelInput channel="hex" asChild>
		<Input name="hex" className={colorInputCls} />
	</ColorPickerPrimitive.ChannelInput>
)

const InputsRgb = () => {
	const { value, setValue } = useContext(ColorPickerValueContext)

	let parsedValue: ReturnType<typeof parseColor> | null = null
	try {
		parsedValue = parseColor(value).toFormat('rgba')
	} catch {
		parsedValue = null
	}

	return (
		<div className="flex items-center justify-center gap-2">
			{(['red', 'green', 'blue'] as const).map((channel) => {
				const channelValue = parsedValue
					? parsedValue.getChannelValue(channel)
					: ''

				return (
					<ColorPickerPrimitive.ChannelInput
						key={channel}
						channel={channel}
						asChild
					>
						<Input
							name={channel}
							value={channelValue}
							disabled={!parsedValue}
							onChange={({ target: { value } }) => {
								if (!parsedValue) return
								const newColor = parsedValue.withChannelValue(
									channel,
									Number(value),
								)
								setValue(newColor.toString('hex'))
							}}
							className={cn(colorInputCls, 'hide-number-input-buttons')}
							containerClassName="w-full"
						/>
					</ColorPickerPrimitive.ChannelInput>
				)
			})}
		</div>
	)
}

const InputsHsl = () => (
	<div className="flex items-center justify-center gap-2">
		{(['hue', 'saturation', 'lightness'] as const).map((channel) => (
			<ColorPickerPrimitive.ChannelInput
				key={channel}
				channel={channel}
				asChild
			>
				<Input
					name={channel}
					className={cn(colorInputCls, 'hide-number-input-buttons')}
					containerClassName="w-full"
				/>
			</ColorPickerPrimitive.ChannelInput>
		))}
	</div>
)

const formatToInputMap = {
	hex: <InputHex />,
	rgb: <InputsRgb />,
	hsl: <InputsHsl />,
} satisfies Record<TColorInputFormat, ReactElement>

export const ChannelInputs = () => {
	const [format, setFormat] = useContext(ColorPickerFormatContext)

	const handleClickLabel = () => {
		if (format === 'hex') {
			setFormat('rgb')
		} else if (format === 'rgb') {
			setFormat('hsl')
		} else {
			setFormat('hex')
		}
	}

	return (
		<ColorPickerPrimitive.View
			format="hsla"
			className="flex items-center justify-between gap-4"
		>
			<button
				onClick={handleClickLabel}
				className="type-body-medium flex cursor-pointer select-none items-center gap-2 uppercase"
			>
				{format}
				<IconSelect />
			</button>
			<div className="w-[15.5rem]">{formatToInputMap[format]}</div>
		</ColorPickerPrimitive.View>
	)
}
